/**
 * @format
 */
import { DOCUMENT, Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { StateObject, Transition, UIRouter } from '@uirouter/core';
import {
    CustomAttainmentWorkflow,
    DegreeProgrammeAttainmentWorkflow,
    ModuleAttainmentApplication,
    ModuleContentWorkflow,
    Plan,
    PriorLearningSubstitutionWorkflow,
    StudentApplication,
} from 'common-typescript/types';
import * as _ from 'lodash-es';
import { firstValueFrom } from 'rxjs';
import { LocaleService } from 'sis-common';
import {
    CodePanelComponent,
    ConfirmDialogService,
    CourseUnitEntityService,
    EducationEntityService,
    getInjectables,
    ModuleEntityService,
    PlanEntityService,
    SisStateDeclaration,
    STUDENT_WORKFLOW_APPLICATION_TYPE,
    STUDENT_WORKFLOW_TYPE,
    StudentApplicationEntityService,
    StudyRightEntityService,
    WorkflowEntityService,
} from 'sis-components';
import { profileSecondaryNavigationView } from '../profile.route-ng';
import { CustomAttainmentApplicationSupplementWizardComponent } from './create/custom-attainment-application-supplement-wizard/custom-attainment-application-supplement-wizard.component';
import { CustomAttainmentWorkflowApplicationWizardComponent } from './create/custom-attainment-workflow-application-wizard/custom-attainment-workflow-application-wizard.component';
import { CreateExtensionApplicationComponent } from './create/extension-application/create-extension-application.component';
import { PriorLearningInclusionWorkflowApplicationWizardComponent } from './create/prior-learning-inclusion-workflow-application-wizard/prior-learning-inclusion-workflow-application-wizard.component';
import { PriorLearningInclusionWorkflowSupplementWizardComponent } from './create/prior-learning-inclusion-workflow-supplement-wizard/prior-learning-inclusion-workflow-supplement-wizard.component';
import { PriorLearningSubstitutionWorkflowApplicationWizardComponent } from './create/prior-learning-substitution-workflow-application-wizard/prior-learning-substitution-workflow-application-wizard.component';
import { PriorLearningSubstitutionWorkflowSupplementWizardComponent } from './create/prior-learning-substitution-workflow-supplement-wizard/prior-learning-substitution-workflow-supplement-wizard.component';
import { CustomAttainmentWorkflowComponent } from './custom-attainment-workflow/custom-attainment-workflow.component';
import { DegreeProgrammeAttainmentWorkflowComponent } from './degree-programme-attainment-workflow/degree-programme-attainment-workflow.component';
import { GraduationWizardComponent } from './graduation-wizard/graduation-wizard.component';
import { ModuleAttainmentApplicationComponent } from './module-attainment-application/module-attainment-application.component';
import { ModuleContentWorkflowComponent } from './module-content-workflow/module-content-workflow.component';
import { PriorLearningWorkflowComponent } from './prior-learning-workflow/prior-learning-workflow.component';
import { StudentApplicationsComponent } from './student-applications/student-applications.component';
import { StudyRightExtensionWorkflowCeeposConfirmationComponent } from './study-right-extension-workflow/ceepos-confirmation/study-right-extension-workflow-ceepos-confirmation.component';
import { StudyRightExtensionWorkflowPaymentFailedComponent } from './study-right-extension-workflow/payment-failed/study-right-extension-workflow-payment-failed.component';
import { StudyRightExtensionWorkflowPaymentProcessingComponent } from './study-right-extension-workflow/payment-processing/study-right-extension-workflow-payment-processing.component';
import { StudyRightExtensionWorkflowPaytrailConfirmationComponent } from './study-right-extension-workflow/paytrail-confirmation/study-right-extension-workflow-paytrail-confirmation.component';
import { StudyRightExtensionWorkflowComponent } from './study-right-extension-workflow/study-right-extension-workflow.component';

const APPLICATION_CREATE_STATE_PREFIX = 'student.logged-in.profile.applications.create-';

export const studentApplicationStates: SisStateDeclaration[] = [
    {
        name: `${APPLICATION_CREATE_STATE_PREFIX}study-right-extension-application`,
        url: '/study-right-extension?planId&studyRightId',
        headerParams: {
            displayNameFunction: extensionApplicationDisplayName,
        },
        data: {
            browserTabTitle: 'STUDENT_APPLICATIONS.TYPE.StudyRightExtensionApplication',
            showHeader: true,
            headerWithoutBreadcrumbs: true,
        },
        views: {
            '': {
                component: CreateExtensionApplicationComponent,
            },
        },
        resolve: {
            plan: resolvePlan,
            studyRight: resolveStudyRight,
            education: resolveEducation,
        },
    },
    {
        name: `${APPLICATION_CREATE_STATE_PREFIX}custom-attainment-application`,
        url: '/custom-attainment?planId&customStudyDraftId',
        headerParams: {
            displayNameKey: `STUDENT_APPLICATIONS.TYPE.${STUDENT_WORKFLOW_APPLICATION_TYPE.CUSTOM_ATTAINMENT_APPLICATION}`,
        },
        data: {
            showHeader: true,
            headerWithoutBreadcrumbs: true,
        },
        views: {
            '': {
                component: CustomAttainmentWorkflowApplicationWizardComponent,
            },
        },
        resolve: {
            plan: findPlan,
            customStudyDraftId: resolveCustomStudyDraftId,
        },
    },
    {
        name: 'student.logged-in.profile.applications.custom-attainment-workflow.supplement',
        url: '/supplement',
        focusMode: {
            defaultExitState: '^',
            titleKey: 'STUDENT_APPLICATIONS.TYPE.CustomAttainmentWorkflow',
        },
        data: {
            showHeader: false,
            browserTabTitle: 'STUDENT_APPLICATIONS.TYPE.CustomAttainmentWorkflow',
        },
        views: {
            '@': {
                component: CustomAttainmentApplicationSupplementWizardComponent,
            },
        },
        resolve: {
            workflow: resolveStudentWorkflow,
        },
        onExit: onSupplementWizardExit,
    },
    {
        name: `${APPLICATION_CREATE_STATE_PREFIX}prior-learning-inclusion-application`,
        url: '/prior-learning-inclusion/?planId&customStudyDraftId',
        headerParams: {
            displayNameKey: `STUDENT_APPLICATIONS.TYPE.${STUDENT_WORKFLOW_APPLICATION_TYPE.PRIOR_LEARNING_INCLUSION_APPLICATION}`,
        },
        data: {
            showHeader: true,
            headerWithoutBreadcrumbs: true,
        },
        views: {
            '': {
                component: PriorLearningInclusionWorkflowApplicationWizardComponent,
            },
        },
        resolve: {
            plan: findPlan,
            customStudyDraftId: resolveCustomStudyDraftId,
        },
    },
    {
        name: 'student.logged-in.profile.applications.prior-learning-application.supplement-prior-learning-inclusion-workflow',
        url: '/inclusion/supplement',
        focusMode: {
            defaultExitState: '^',
            titleKey: 'STUDENT_APPLICATIONS.TYPE.PriorLearningInclusionWorkflow',
        },
        data: {
            showHeader: false,
            browserTabTitle: 'STUDENT_APPLICATIONS.TYPE.PriorLearningInclusionWorkflow',
        },
        views: {
            '@': {
                component: PriorLearningInclusionWorkflowSupplementWizardComponent,
            },
        },
        resolve: {
            workflow: resolveStudentWorkflow,
        },
        onExit: onSupplementWizardExit,
    },
    {
        name: `${APPLICATION_CREATE_STATE_PREFIX}prior-learning-substitution-application`,
        url: '/prior-learning-substitution/?planId&courseUnitId',
        headerParams: {
            displayNameFunction: transition => {
                const [localeService, translate, courseUnit] = getInjectables(transition, LocaleService, TranslateService, 'courseUnit');
                const applicationTypeName = translate.instant(
                    `STUDENT_APPLICATIONS.TYPE.${STUDENT_WORKFLOW_APPLICATION_TYPE.PRIOR_LEARNING_SUBSTITUTION_APPLICATION}`,
                );
                return `${applicationTypeName}: ${localeService.localize(courseUnit?.name)}`;
            },
        },
        data: {
            showHeader: true,
            headerWithoutBreadcrumbs: true,
        },
        views: {
            '': {
                component: PriorLearningSubstitutionWorkflowApplicationWizardComponent,
            },
        },
        resolve: {
            plan: findPlan,
            courseUnit: findCourseUnit,
        },
    },
    {
        name: 'student.logged-in.profile.applications.prior-learning-application.supplement-prior-learning-substitution-workflow',
        url: '/substitution/supplement',
        focusMode: {
            defaultExitState: '^',
            titleKey: 'STUDENT_APPLICATIONS.TYPE.PriorLearningSubstitutionWorkflow',
        },
        data: {
            showHeader: false,
            browserTabTitle: 'STUDENT_APPLICATIONS.TYPE.PriorLearningSubstitutionWorkflow',
        },
        views: {
            '@': {
                component: PriorLearningSubstitutionWorkflowSupplementWizardComponent,
            },
        },
        resolve: {
            workflow: resolveStudentWorkflow,
        },
        onExit: onSupplementWizardExit,
    },
    {
        name: 'student.logged-in.profile.applications.degree-programme-attainment-workflow',
        url: '/degree-programme/:applicationId',
        views: {
            '': {
                component: DegreeProgrammeAttainmentWorkflowComponent,
            },
            'codepanel@': {
                component: CodePanelComponent,
            },
        },
        headerParams: {
            displayNameFunction: transition => {
                const [localeService, translate, degreeProgramme] = getInjectables(
                    transition,
                    LocaleService,
                    TranslateService,
                    'degreeProgramme',
                );
                const applicationTypeName = translate.instant('STUDENT_APPLICATIONS.TYPE.DegreeProgrammeAttainmentApplication');
                return `${applicationTypeName}: ${localeService.localize(degreeProgramme?.name)}`;
            },
        },
        resolve: {
            studentWorkflow: resolveStudentWorkflow,
            degreeProgramme: resolveWorkflowDegreeProgramme,
            code: resolveWorkflowCode,
        },
        data: {
            showHeader: true,
        },
    },
    {
        name: 'student.logged-in.profile.applications.custom-attainment-workflow',
        url: '/custom-attainment/:applicationId',
        views: {
            '': {
                component: CustomAttainmentWorkflowComponent,
            },
            'codepanel@': {
                component: CodePanelComponent,
            },
        },
        headerParams: {
            displayNameFunction: transition => {
                const [translate, studentWorkflow] = getInjectables(transition, TranslateService, 'studentWorkflow');
                const applicationTypeName = translate.instant('STUDENT_APPLICATIONS.TYPE.CustomAttainmentWorkflow');
                return `${applicationTypeName}: ${(studentWorkflow as CustomAttainmentWorkflow).name}`;
            },
        },
        resolve: {
            studentWorkflow: resolveStudentWorkflow,
            code: resolveWorkflowCode,
        },
        data: {
            showHeader: true,
        },
    },
    {
        name: 'student.logged-in.profile.applications.study-right-extension-workflow',
        url: '/study-right-extension/:applicationId',
        views: {
            '': {
                component: StudyRightExtensionWorkflowComponent,
            },
            'codepanel@': {
                component: CodePanelComponent,
            },
        },
        headerParams: {
            displayNameFunction: transition => {
                const [translate, localeService, education] = getInjectables(transition, TranslateService, LocaleService, 'education');
                const applicationTypeName = translate.instant('STUDENT_APPLICATIONS.TYPE.StudyRightExtensionWorkflow');
                return `${applicationTypeName}: ${localeService.localize(education?.name)}`;
            },
        },
        resolve: {
            studentWorkflow: resolveStudentWorkflow,
            education: resolveWorkflowEducation,
            code: resolveWorkflowCode,
        },
        data: {
            showHeader: true,
            showApplicationStateBadge: true,
        },
    },
    {
        name: 'student.logged-in.profile.applications.study-right-extension-workflow-ceepos-confirmation',
        url: '/study-right-extension/ceepos-confirmation',
        views: {
            '': { component: StudyRightExtensionWorkflowCeeposConfirmationComponent },
        },
        data: {
            showHeader: false,
        },
    },
    {
        name: 'student.logged-in.profile.applications.study-right-extension-workflow-paytrail-confirmation',
        url: '/study-right-extension/paytrail-confirmation',
        views: {
            '': { component: StudyRightExtensionWorkflowPaytrailConfirmationComponent },
        },
        data: {
            showHeader: false,
        },
    },
    {
        name: 'student.logged-in.profile.applications.study-right-extension-workflow-payment-failed',
        url: '/study-right-extension/payment-failed',
        views: {
            '': { component: StudyRightExtensionWorkflowPaymentFailedComponent },
        },
        data: {
            showHeader: false,
        },
    },
    {
        name: 'student.logged-in.profile.applications.study-right-extension-workflow-payment-processing',
        url: '/study-right-extension/payment-processing',
        views: {
            '': { component: StudyRightExtensionWorkflowPaymentProcessingComponent },
        },
        data: {
            showHeader: false,
        },
    },
    {
        name: 'student.logged-in.profile.applications.module-content-application',
        url: '/module-content-application/:applicationId',
        views: {
            '': {
                component: ModuleContentWorkflowComponent,
            },
            'codepanel@': {
                component: CodePanelComponent,
            },
        },
        headerParams: {
            displayNameFunction: transition => {
                const [translate, localeService, workflow, education] = getInjectables(
                    transition,
                    TranslateService,
                    LocaleService,
                    'studentWorkflow',
                    'education',
                );
                const applicationTypeName = translate.instant(`STUDENT_APPLICATIONS.TYPE.${workflow.type}`);
                return `${applicationTypeName}: ${localeService.localize(education?.name)}`;
            },
        },
        resolve: {
            studentWorkflow: resolveStudentWorkflow,
            studentModule: resolveWorkflowStudyModule,
            education: resolveWorkflowEducation,
            code: resolveWorkflowCode,
        },
        data: {
            showHeader: true,
        },
    },
    {
        name: 'student.logged-in.profile.applications.prior-learning-application',
        url: '/prior-learning/:applicationId',
        views: {
            '': {
                component: PriorLearningWorkflowComponent,
            },
            'codepanel@': {
                component: CodePanelComponent,
            },
        },
        headerParams: {
            displayNameFunction: resolveWorkflowDisplayName,
        },
        resolve: {
            studentWorkflow: resolveStudentWorkflow,
            courseUnit: resolveWorkflowCourseUnit,
            code: resolveWorkflowCode,
        },
        data: {
            showHeader: true,
        },
    },
    {
        name: 'student.logged-in.profile.applications.module-attainment-application',
        url: '/module-attainment/:applicationId',
        views: {
            '': {
                component: ModuleAttainmentApplicationComponent,
            },
            'codepanel@': {
                component: CodePanelComponent,
            },
        },
        headerParams: {
            displayNameFunction: transition => {
                const [translate, localeService, studyModule] = getInjectables(transition, TranslateService, LocaleService, 'studyModule');
                const applicationTypeName = translate.instant('STUDENT_APPLICATIONS.TYPE.ModuleAttainmentApplication');
                return `${applicationTypeName}: ${localeService.localize(studyModule?.name)}`;
            },
        },
        resolve: {
            application: resolveStudentApplication,
            studyModule: resolveApplicationStudyModule,
            code: resolveApplicationCode,
        },
        data: {
            showHeader: true,
            showApplicationStateBadge: true,
        },
    },
    {
        name: 'student.logged-in.profile.applications.list',
        url: '/list',
        views: {
            '': { component: StudentApplicationsComponent },
            // applications.list IS the Applications tab, so it keeps the profile secondary navigation.
            ...profileSecondaryNavigationView,
        },
        headerParams: {
            displayPageTitleNameKey: 'MY_INFORMATION',
            skipBreadcrumb: true,
        },
    },
    {
        name: 'student.logged-in.graduation-wizard',
        url: '/graduation-wizard?planId&degreeProgrammeId&step',
        component: GraduationWizardComponent,
        focusMode: {
            defaultExitState: 'student.logged-in.frontpage',
            titleKey: 'GRADUATION_WIZARD.TITLE',
        },
    },
];

function getTransitionPath(state: StateObject): string {
    return (state?.path ?? [])
        .map(path => path?.url?.pattern ?? '')
        .join('')
        .replace(/\?.*/, '');
}

/**
 * Registers a UI-Router transition hook which shows a confirmation dialog if the user attempts to navigate from an unsaved application
 * without saving to any other state except the application view state (which is shown after the application is submitted).
 */
export function registerApplicationDiscardConfirmationHook(uiRouter: UIRouter, injector: Injector) {
    uiRouter.transitionService.onBefore({ from: state => state.name.startsWith(APPLICATION_CREATE_STATE_PREFIX) }, transition => {
        // Ignore redirects, otherwise the modal will be shown several times
        if (!transition.redirectedFrom() && !transition.options()?.custom?.skipConfirmationDialog) {
            const fromPath = getTransitionPath(transition.$from());
            const toPath = getTransitionPath(transition.$to());

            // The student application URLs are constructed so that the start of the URL is the same
            // for create and view states, with the former being shorter. So by checking if the start
            // of the path is the same, we know if the user is navigating to the application view
            // state or somewhere else.
            if (!fromPath || !toPath || !toPath.startsWith(fromPath)) {
                return injector
                    .get(ConfirmDialogService)
                    .confirm(
                        {
                            title: 'PROFILE.APPLICATIONS.CONFIRM_DISCARD_APPLICATION.TITLE',
                            description: 'PROFILE.APPLICATIONS.CONFIRM_DISCARD_APPLICATION.BODY',
                        },
                        true,
                    )
                    .catch(() => {
                        const window = injector.get(DOCUMENT).defaultView;
                        const newUrl = window.location.pathname + window.location.search;
                        const currentUrl = uiRouter.stateService.href(uiRouter.globals.$current);
                        if (newUrl !== currentUrl) {
                            // The browser already changed URL; undo it. This "cancels" browser back button
                            // clicks and other non-ui-router transitions.
                            window.history.replaceState(null, null, currentUrl);
                        }
                        return false;
                    });
            }
        }
    });
}

function extensionApplicationDisplayName(transition: Transition): string {
    const [$translate, localeService, education] = getInjectables(transition, '$translate', 'localeService', 'education');
    const applicationTypeName = $translate.instant('STUDENT_APPLICATIONS.TYPE.StudyRightExtensionApplication');
    return `${applicationTypeName}: ${localeService.getLocalizedValue(education?.name)}`;
}

extensionApplicationDisplayName.$inject = ['$transition$'];

export function resolveWorkflowDisplayName(transition: Transition) {
    const [translate, localeService, workflow] = getInjectables(transition, TranslateService, LocaleService, 'studentWorkflow');
    const workflowTypeName = translate.instant(`STUDENT_APPLICATIONS.TYPE.${workflow.type}`);
    switch (workflow.type) {
        case STUDENT_WORKFLOW_TYPE.PRIOR_LEARNING_SUBSTITUTION_WORKFLOW: {
            const [courseUnit] = getInjectables(transition, 'courseUnit');
            return `${workflowTypeName}: ${localeService.localize(courseUnit?.name)}`;
        }
        case STUDENT_WORKFLOW_TYPE.PRIOR_LEARNING_INCLUSION_WORKFLOW:
            return `${workflowTypeName}: ${workflow.name}`;
        default:
            return null;
    }
}

resolveWorkflowDisplayName.$inject = ['$transition$'];

export function findCourseUnit(transition: Transition) {
    return transition.injector().get('commonCourseUnitService').findById(transition.params().courseUnitId, { loadRelations: true });
}

findCourseUnit.$inject = ['$transition$'];

export function findPlan(transition: Transition) {
    return transition.injector().get('commonPlanService').findById(transition.params().planId, true);
}

findPlan.$inject = ['$transition$'];

export function resolvePlan(transition: Transition) {
    return firstValueFrom(transition.injector().get(PlanEntityService).getById(transition.params().planId));
}

resolvePlan.$inject = ['$transition$'];

export function resolveStudyRight(transition: Transition) {
    return firstValueFrom(transition.injector().get(StudyRightEntityService).getById(transition.params().studyRightId, true));
}

resolveStudyRight.$inject = ['$transition$'];

export function resolveEducation(transition: Transition, plan: Plan) {
    return firstValueFrom(transition.injector().get(EducationEntityService).getById(plan.rootId, true));
}

resolveEducation.$inject = ['$transition$', 'plan'];

export function getPlanValidationResult(transition: Transition) {
    return transition.injector().get('PlanLoader').validatePlan(transition.params().planId);
}

getPlanValidationResult.$inject = ['$transition$'];

export function resolvePlanValidationResult(transition: Transition, plan: any) {
    return transition.injector().get('PlanLoader').validatePlanWithThisPlan(plan);
}

resolvePlanValidationResult.$inject = ['$transition$', 'plan'];

export function resolveValidatablePlan(planValidationResult: any) {
    return _.get(planValidationResult, 'validatablePlan');
}

resolveValidatablePlan.$inject = ['planValidationResult'];

export function resolveCustomStudyDraftId(transition: Transition) {
    return transition.params().customStudyDraftId;
}

resolveCustomStudyDraftId.$inject = ['$transition$'];

export function degreeProgrammeResolver(validatablePlan: any, $transition$: any) {
    return _.get(validatablePlan.modulesById, $transition$.params().degreeProgrammeId);
}

degreeProgrammeResolver.$inject = ['validatablePlan', '$transition$'];

export function resolveStudentWorkflow($transition$: Transition) {
    const [workflowEntityService] = getInjectables($transition$, WorkflowEntityService);
    const workflowId = $transition$.params().applicationId;
    return firstValueFrom(workflowEntityService.getById(workflowId));
}

resolveStudentWorkflow.$inject = ['$transition$'];

export function resolveWorkflowDegreeProgramme($transition$: Transition, studentWorkflow: DegreeProgrammeAttainmentWorkflow) {
    const [moduleEntityService] = getInjectables($transition$, ModuleEntityService);
    return firstValueFrom(moduleEntityService.getById(studentWorkflow.moduleId));
}

resolveWorkflowDegreeProgramme.$inject = ['$transition$', 'studentWorkflow'];

export function resolveWorkflowStudyModule($transition$: Transition, studentWorkflow: ModuleContentWorkflow) {
    const [moduleEntityService] = getInjectables($transition$, ModuleEntityService);
    return firstValueFrom(moduleEntityService.getById(studentWorkflow.approvedModuleId));
}

resolveWorkflowStudyModule.$inject = ['$transition$', 'studentWorkflow'];

export function resolveWorkflowEducation($transition$: Transition, studentWorkflow: ModuleContentWorkflow) {
    const [educationEntityService] = getInjectables($transition$, EducationEntityService);
    return firstValueFrom(educationEntityService.getById(studentWorkflow.educationId));
}

resolveWorkflowEducation.$inject = ['$transition$', 'studentWorkflow'];

export function resolveWorkflowCourseUnit($transition$: Transition, studentWorkflow: PriorLearningSubstitutionWorkflow) {
    const [courseUnitEntityService] = getInjectables($transition$, CourseUnitEntityService);
    if (!studentWorkflow.courseUnitId) return null;
    return firstValueFrom(courseUnitEntityService.getById(studentWorkflow.courseUnitId));
}

resolveWorkflowCourseUnit.$inject = ['$transition$', 'studentWorkflow'];

export function resolveWorkflowCode($transition$: Transition, studentWorkflow: ModuleContentWorkflow) {
    return studentWorkflow.code;
}

resolveWorkflowCode.$inject = ['$transition$', 'studentWorkflow'];

export function resolveStudentApplication($transition$: Transition) {
    const [studentApplicationEntityService] = getInjectables($transition$, StudentApplicationEntityService);
    return firstValueFrom(studentApplicationEntityService.findById($transition$.params().applicationId, true));
}

resolveStudentApplication.$inject = ['$transition$'];

export function resolveApplicationStudyModule($transition$: Transition, application: ModuleAttainmentApplication) {
    const [moduleEntityService] = getInjectables($transition$, ModuleEntityService);
    return firstValueFrom(moduleEntityService.getById(application.moduleId));
}

resolveApplicationStudyModule.$inject = ['$transition$', 'application'];

export function resolveApplicationCode(application: StudentApplication) {
    return application.code;
}

resolveApplicationCode.$inject = ['application'];

export function onSupplementWizardExit($transition$: Transition, $state: any) {
    const confirmDialogService = $transition$.injector().get(ConfirmDialogService);
    if ($transition$.$to().name === $transition$.$from().name) {
        return;
    }
    if ($transition$.options().custom.skipConfirmationDialog) {
        return;
    }

    // These translations are shared with application create wizards
    const confirmModalTexts = {
        title: 'PROFILE.APPLICATIONS.CONFIRM_DISCARD_APPLICATION.TITLE',
        description: 'PROFILE.APPLICATIONS.CONFIRM_DISCARD_APPLICATION.BODY',
    };
    return confirmDialogService
        .confirm(confirmModalTexts, true)
        .then(() => {})
        .catch(() => {
            /* Same trick as in create wizards, if the browser already changed the url
                this will undo it */
            const { location } = window;
            if (location.pathname + location.search !== $state.href($state.$current)) {
                window.history.replaceState(null, null, $state.href($state.$current));
            }
            return false;
        });
}

onSupplementWizardExit.$inject = ['$transition$', '$state'];
